// Vite entry. Builds the global namespace the rest of the prototype expects.
//
// The prototype was written without modules — every file ended in
// `Object.assign(window, { Foo, Bar })` and other files referenced those
// names as globals. The migration keeps that contract: each source file now
// `export`s the same symbols, and here we import them in dependency order
// and re-attach them to `window` so downstream code keeps working unchanged.
//
// `React` itself is needed as a global because some files reference
// `React.useState`, `React.Fragment`, etc. without explicit imports.
// `React` here is provided by vite.config's esbuild.jsxInject, so no
// explicit import is needed (and a redundant one would collide).

import ReactDOM from 'react-dom/client';

import '../styles/tokens.css';
import { AuthProvider } from './lib/auth.jsx';

window.React = React;
window.ReactDOM = ReactDOM;

// Data first — screens read from `window.PAPERS` etc. at module evaluation.
import { PAPERS, BENCHMARKS, PROJECT_BOARD } from '../data/papers.js';
import { QUESTIONS, QA_CATEGORIES } from '../data/questions.js';
Object.assign(window, { PAPERS, BENCHMARKS, PROJECT_BOARD, QUESTIONS, QA_CATEGORIES });

// Tweaks shell + primitives.
import * as TweaksModule from '../tweaks-panel.jsx';
Object.assign(window, TweaksModule);

// i18n + themes — depended on by every screen.
import { I18N, LangContext, LangProvider, useT } from '../components/i18n.jsx';
import { THEMES, applyTheme } from '../components/themes.jsx';
Object.assign(window, { I18N, LangContext, LangProvider, useT, THEMES, applyTheme });

// Shared primitives (icons → primitives → panels → shell → palettes/detail).
import { I, ICONS } from '../components/icons.jsx';
Object.assign(window, { I, ICONS });

import {
  ScoreArc, Gauge, ScoreBar, Sparkline, KPI, Avatars, PaperCard,
  ScoreInline, SectionHead,
} from '../components/primitives.jsx';
Object.assign(window, {
  ScoreArc, Gauge, ScoreBar, Sparkline, KPI, Avatars, PaperCard,
  ScoreInline, SectionHead,
});

import {
  RailRight, RailLeft, RailCollapseBtn, useRailControls,
} from '../components/panels.jsx';
Object.assign(window, { RailRight, RailLeft, RailCollapseBtn, useRailControls });

import {
  Sidebar, TopBar, Breadcrumb, NAV_PRIMARY, PROJECTS, NavContext,
} from '../components/shell.jsx';
Object.assign(window, { Sidebar, TopBar, Breadcrumb, NAV_PRIMARY, PROJECTS, NavContext });

import { CommandPalette } from '../components/command-palette.jsx';
Object.assign(window, { CommandPalette });

import * as DetailSections from '../components/detail-sections.jsx';
Object.assign(window, DetailSections);

// Screens.
import { DashboardScreen } from '../screens/01-dashboard.jsx';
import { ExploreScreen } from '../screens/02-explore.jsx';
import { DetailScreen } from '../screens/03-detail.jsx';
import { ReaderScreen } from '../screens/04-reader.jsx';
import { BenchScreen } from '../screens/05-bench.jsx';
import { ProjectBoardScreen } from '../screens/06-projects.jsx';
import { GraphScreen } from '../screens/07-graph.jsx';
import { WikiScreen } from '../screens/08-wiki.jsx';
import { AssistantScreen } from '../screens/09-assistant.jsx';
import { SettingsScreen } from '../screens/10-settings.jsx';
import { QuestionsScreen } from '../screens/11-questions.jsx';
Object.assign(window, {
  DashboardScreen, ExploreScreen, DetailScreen, ReaderScreen, BenchScreen,
  ProjectBoardScreen, GraphScreen, WikiScreen, AssistantScreen,
  SettingsScreen, QuestionsScreen,
});

// App is loaded last because it references everything above.
import { PaperPilotApp, TWEAK_DEFAULTS, SCREENS, VIEWPORTS } from '../app.jsx';
Object.assign(window, { PaperPilotApp, TWEAK_DEFAULTS, SCREENS, VIEWPORTS });

ReactDOM.createRoot(document.getElementById('root')).render(
  <AuthProvider>
    <PaperPilotApp />
  </AuthProvider>,
);
